import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import {
  CreateDriverPatientDistributionInput,
  DistributionMode,
  GqlService,
  listDriverPatientDistributionsQuery,
  UpdateDriverPatientDistributionInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { PatientsStore } from './patients.store';

@Injectable({
  providedIn: 'root',
})
export class PatientsService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private patientStore = inject(PatientsStore);

  async createPatient(patient: CreateDriverPatientDistributionInput) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createDriverPatientDistribution$(patient)
    );

    if (!(success && data)) {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  get(id?: string) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.patientStore.setLoading(true);

        return this.gqlService.listDriverPatientDistributions$(id);
      }),
      tap(({ data, errors, success }) => {
        if (success && data) {
          data.forEach((eachData) => {
            // All forecasts normally have id unless they were not created from csv beforehand. So
            // this is just for making store does its jobs correctly and these id's are not saved at anywhere.
            // if there is no id assigned beforehand, store fills itself with only last value. These assigned id's are not used anywhere.
            if (!eachData.forecast.id) {
              eachData.forecast.id = uuidv4();
            }

            if (eachData.actual == null) {
              eachData.actual = {
                __typename: 'PatientEnrollmentData',
                cumulative_enrollment_percentage: 0,
                distribution_mode: DistributionMode.DISTRIBUTION_MODE_ACTUAL,
                net_patients_enrolled: 0,
                patients_complete: 0,
                patients_discontinued: 0,
                patients_enrolled: 0,
                total_patients_enrolled: 0,
              };
            }
          });
          const bData = (data || []).map((val) => {
            return { ...val, id: val.forecast.id };
          });

          this.patientStore.set(bData);
        } else {
          this.overlayService.error(errors, undefined, true);
        }

        this.patientStore.setLoading(false);
      })
    );
  }

  async updatePatientDistribution(x: UpdateDriverPatientDistributionInput) {
    this.patientStore.setLoading(true);
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateDriverPatientDistribution$({
        cumulative_enrollment_percentage: x.cumulative_enrollment_percentage,
        distribution_mode: x.distribution_mode,
        distribution_month: x.distribution_month,
        id: x.id,
        net_patients_enrolled: x.net_patients_enrolled,
        patients_complete: x.patients_complete,
        patients_discontinued: x.patients_discontinued,
        patients_enrolled: x.patients_enrolled,
        total_patients_enrolled: x.total_patients_enrolled,
      })
    );

    if (success && data) {
      const updatedPD = {
        __typename: 'DriverPatientDistribution',
        distribution_mode: data.forecast.distribution_mode,
        forecast: {
          __typename: 'PatientEnrollmentData',
          cumulative_enrollment_percentage: data.forecast.cumulative_enrollment_percentage,
          distribution_month: data.distribution_month,
          id: data.forecast.id,
          net_patients_enrolled: data.forecast.net_patients_enrolled,
          patients_complete: data.forecast.patients_complete,
          patients_discontinued: data.forecast.patients_discontinued,
          patients_enrolled: data.forecast.patients_enrolled,
          total_patients_enrolled: data.forecast.total_patients_enrolled,
        },
      } as unknown as Partial<listDriverPatientDistributionsQuery>;

      this.patientStore.update(
        (entity: listDriverPatientDistributionsQuery) => entity.forecast.id === data.forecast.id,
        updatedPD
      );
      this.patientStore.setLoading(false);

      return true;
    }

    this.overlayService.error(errors);

    this.patientStore.setLoading(false);

    return false;
  }
}
